/**
 * Kitchen view of who is booked for today's meal-plan meals.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { MealPeriod, MealPlanWeek, WeeklyBooking } from '../../types';
import { customersService, mealPlanWeeksService, weeklyBookingsService } from '../../lib/database';
import { mealPeriodText, nzDateKey, offeredPeriods, weekIdContaining } from '../../lib/mealPlanWeek';

const PERIOD_LABEL: Record<MealPeriod, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
};

function statusTone(status: string): string {
  if (status === 'redeemed') return 'bg-emerald-100 text-emerald-900';
  if (status === 'cancelled' || status === 'no_show') return 'bg-stone-200 text-stone-600';
  return 'bg-amber-100 text-amber-950';
}

const KitchenMealPlanBookingsPanel: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const dayKey = nzDateKey();
  const weekId = weekIdContaining();
  const [week, setWeek] = useState<MealPlanWeek | null>(null);
  const [bookings, setBookings] = useState<WeeklyBooking[]>([]);
  const [namesById, setNamesById] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodFilter, setPeriodFilter] = useState<MealPeriod | 'all'>('all');
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [weekDoc, weekBookings, customers] = await Promise.all([
        mealPlanWeeksService.getById(weekId),
        weeklyBookingsService.getByWeekId(weekId),
        customersService.getAll().catch(() => []),
      ]);
      setWeek(weekDoc);
      const today = weekBookings.filter((row) => row.dayKey === dayKey && row.status !== 'cancelled');
      setBookings(today);
      const names: Record<string, string> = {};
      for (const customer of customers) {
        names[String(customer.id)] = customer.name;
      }
      setNamesById(names);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load meal plan bookings.');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [dayKey, weekId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const offered = offeredPeriods(week?.periods);
  const dayMenu = week?.menu?.[dayKey];
  const grouped = useMemo(() => {
    const rows = periodFilter === 'all' ? bookings : bookings.filter((row) => row.period === periodFilter);
    const byPeriod: Record<MealPeriod, WeeklyBooking[]> = {
      breakfast: [],
      lunch: [],
      dinner: [],
    };
    for (const row of rows) {
      if (byPeriod[row.period]) byPeriod[row.period].push(row);
    }
    for (const period of offered) {
      byPeriod[period].sort((a, b) => {
        const left = (namesById[String(a.customerId)] || a.customerEmail || '').localeCompare(
          namesById[String(b.customerId)] || b.customerEmail || '',
        );
        if (left !== 0) return left;
        return a.status.localeCompare(b.status);
      });
    }
    return byPeriod;
  }, [bookings, namesById, offered, periodFilter]);

  const periodsToShow = periodFilter === 'all' ? offered : offered.filter((period) => period === periodFilter);
  const bookedCount = bookings.filter((row) => row.status === 'booked').length;
  const redeemedCount = bookings.filter((row) => row.status === 'redeemed').length;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-stone-100">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-stone-300 bg-stone-900 px-3 py-2 text-white">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.14em] text-amber-300">Meal plans today</p>
          <p className="truncate text-sm font-semibold">
            {dayKey} · {bookedCount} still to collect · {redeemedCount} picked up
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setRefreshKey((n) => n + 1)}
            className="rounded-md bg-stone-700 px-3 py-1.5 text-sm font-medium hover:bg-stone-600"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={onBack}
            className="rounded-md bg-slate-700 px-3 py-1.5 text-sm font-medium hover:bg-slate-600"
          >
            Back to Kitchen
          </button>
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap gap-2 border-b border-stone-200 bg-white px-3 py-2">
        <button
          type="button"
          onClick={() => setPeriodFilter('all')}
          className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
            periodFilter === 'all' ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-700'
          }`}
        >
          All ({bookings.length})
        </button>
        {offered.map((period) => {
          const count = bookings.filter((row) => row.period === period).length;
          return (
            <button
              key={period}
              type="button"
              onClick={() => setPeriodFilter(period)}
              className={`rounded-full px-3 py-1.5 text-sm font-semibold capitalize ${
                periodFilter === period ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-700'
              }`}
            >
              {PERIOD_LABEL[period]} ({count})
            </button>
          );
        })}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3 space-y-3">
        {loading && <p className="text-sm text-stone-600">Loading bookings…</p>}
        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
        )}
        {!loading && !error && bookings.length === 0 && (
          <div className="rounded-xl border border-dashed border-stone-300 bg-white px-4 py-8 text-center text-sm text-stone-600">
            Nobody is booked for meal plans today.
          </div>
        )}
        {!loading &&
          periodsToShow.map((period) => {
            const rows = grouped[period];
            const dish = mealPeriodText(dayMenu, period);
            return (
              <section key={period} className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                <header className="border-b border-stone-100 bg-stone-50 px-4 py-3">
                  <h3 className="text-base font-bold text-stone-900">{PERIOD_LABEL[period]}</h3>
                  <p className="mt-1 text-sm text-stone-600">
                    {dish || 'No meal written for this period yet.'} · {rows.length} cover{rows.length === 1 ? '' : 's'}
                  </p>
                </header>
                {rows.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-stone-500">No one booked for {PERIOD_LABEL[period].toLowerCase()}.</p>
                ) : (
                  <ul className="divide-y divide-stone-100">
                    {rows.map((row) => {
                      const name = namesById[String(row.customerId)] || row.customerEmail || 'Customer';
                      return (
                        <li key={row.id} className="flex items-center justify-between gap-3 px-4 py-3">
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-stone-900">{name}</p>
                            {row.customerEmail && name !== row.customerEmail && (
                              <p className="truncate text-xs text-stone-500">{row.customerEmail}</p>
                            )}
                          </div>
                          <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${statusTone(row.status)}`}>
                            {row.status.replace('_', ' ')}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
      </div>
    </div>
  );
};

export default KitchenMealPlanBookingsPanel;
